'use  client';

import { useState, useEffect } from 'react';
import {
  useSpring,
  animated,
  useReducedMotion,
  Globals,
} from '@react-spring/web';
import styles from './ProductTour.module.scss';
import MiniPagination from './MiniPagination';
import { optimizeImage } from 'src/lib/image-helpers';
import GalleryToggle from './GalleryToggle';
import cx from 'classnames';

type Step = {
  id?: number;
  text: string;
  image: string;
};

type ProductTourProps = {
  steps: Step[];
  defaultGalleryView?: boolean;
};

type AnimatedCardProps = {
  step: Step;
  index: number;
  style: any;
};

const ProductTour: React.FC<ProductTourProps> = ({
  steps,
  defaultGalleryView = false,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [useGalleryView, setUseGalleryView] = useState(defaultGalleryView);
  const reducedMotion = useReducedMotion();
  useEffect(() => {
    Globals.assign({
      skipAnimation: reducedMotion,
    });

    return () => {
      Globals.assign({
        skipAnimation: false,
      });
    };
  }, [reducedMotion]);

  const pageSelected = (pageNum: number) => {
    setCurrentIndex(pageNum);
    setHasInteracted(true);
  };

  const advance = () => {
    setCurrentIndex((prevIndex) => (prevIndex + 1) % steps.length);
  };

  const goForward = () => {
    advance();
    setHasInteracted(true);
  };

  const goBack = () => {
    setCurrentIndex(
      (prevIndex) => (prevIndex - 1 + steps.length) % steps.length,
    );
    setHasInteracted(true);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowRight') {
        goForward();
      } else if (event.key === 'ArrowLeft') {
        goBack();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  const getStyle = (index: number) => {
    if (index === currentIndex) {
      return {
        transform: 'translate(0%, 0%) scale(1)',
        opacity: 1,
        display: 'flex',
        // width: '320px',
        // height: '655px',
      };
    } else {
      const offset = index - currentIndex;
      return {
        transform: `translate(${offset * 105}%, 0%) scale(0.8)`,
        opacity: 0.5,
        display: 'flex',
      };
    }
  };

  const numRowsColumns = (numSteps: number) => {
    if (numSteps <= 3) {
      return { rows: 1, columns: numSteps };
    } else {
      return { rows: 2, columns: Math.ceil(numSteps / 2) };
    }
  };

  const calculateScale = (rows, columns) => {
    // TODO: In the future, let's not make these hard coded.
    const originalCardWidth = 320;
    const originalCardHeight = 563;
    const containerWidth = 624;
    const containerHeight = 780;

    const targetWidth = containerWidth / columns;
    const targetHeight = containerHeight / rows;

    const scaleX = targetWidth / originalCardWidth;
    const scaleY = targetHeight / originalCardHeight;

    const scale = Math.min(scaleX, scaleY) * 1.1;
    return scale;
  };

  const getGalleryStyle = (index: number) => {
    const { rows, columns } = numRowsColumns(steps.length);
    let row, column, columnsThisRow;
    if (rows === 1) {
      row = 0.5;
      column = index;
      columnsThisRow = columns;
    } else {
      row = Math.floor(index / columns);
      column = index % columns;
      columnsThisRow = row === 0 ? columns : steps.length - columns;
    }
    const OFFSET_X = 40;
    const translateX =
      2 * OFFSET_X * column - OFFSET_X * columnsThisRow + OFFSET_X;
    const translateY = row * 90 - 45;
    const scale = calculateScale(rows, columns);

    return {
      transform: `translate(${translateX * scale}%, ${
        translateY * scale
      }%) scale(${scale})`,
      opacity: 1,
      display: 'flex',
      position: 'absolute',
    };
  };

  // Auto-advance only in "attract" mode — stop once the user has interacted.
  useEffect(() => {
    if (hasInteracted) return;
    const intervalId = setInterval(() => {
      if (!useGalleryView) {
        advance();
      }
    }, 7000);
    return () => clearInterval(intervalId);
  }, [hasInteracted, useGalleryView]);

  const AnimatedCard: React.FC<AnimatedCardProps> = ({
    step,
    index,
    style,
  }) => (
    <animated.div
      style={style}
      className={cx(styles.card)}
      onClick={() => {
        pageSelected(index);
        if (useGalleryView) {
          setUseGalleryView(false);
        }
      }}
      onMouseEnter={() => {
        if (useGalleryView) {
          setCurrentIndex(index);
        }
      }}
    >
      <img
        src={optimizeImage(step.image)}
        className={styles.cardImage}
        alt={step.text}
      />
    </animated.div>
  );

  return (
    <div className={styles.tourWrapper}>
      <div className={styles.topBar}>
        <span> </span>
        <MiniPagination
          totalItems={steps.length}
          currentPage={currentIndex}
          onPageChange={(newIndex) => pageSelected(newIndex)}
          isActive={!useGalleryView}
        />
        <span>
          <GalleryToggle
            useGalleryView={useGalleryView}
            setUseGalleryView={setUseGalleryView}
          />
        </span>
      </div>
      <div className={styles.captionWrapper}>{steps[currentIndex].text}</div>
      <div className={styles.slideshowContainer}>
        {steps.map((step, index) => {
          const style = useSpring(
            useGalleryView ? getGalleryStyle(index) : getStyle(index),
          );
          return (
            <AnimatedCard key={index} step={step} index={index} style={style} />
          );
        })}
      </div>
    </div>
  );
};

export default ProductTour;
